import { conversion } from 'country-configuration/currency'
import { useLabels } from 'labels/hooks/useLabels/useLabels'
import { useMasterData } from 'master-data/hooks/useMasterData/useMasterData'
import { type RangePickerValue } from 'product-list/types/Filters'
import { dataTestIds } from 'product-list/utils/testIds'
import { type CSSProperties, useCallback, useEffect, useMemo } from 'react'
import { cx } from 'utils/cx'

import { Range } from './components/Range/Range'

import styles from './RangePicker.module.scss'

interface Props {
	readonly selectedRange: RangePickerValue
	readonly selectedLimitRange: RangePickerValue
	readonly setSelectedRange: (value: RangePickerValue) => void
	readonly space?: number
}

const ZERO_ITEMS = 0
const ONE_ITEM = 1

const DEFAULT_LIMIT = { min: ZERO_ITEMS, max: ONE_ITEM }
export const SPACE_PERCENTAGE = 0.05

export function RangePicker({
	selectedRange,
	selectedLimitRange,
	setSelectedRange,
}: Props): React.ReactNode {
	const { t } = useLabels()
	const { country } = useMasterData()

	const range = selectedLimitRange.max - selectedLimitRange.min

	const isSameValue = range === 0

	const { price: minPrice } = conversion(selectedRange.min, country)
	const { price: maxPrice } = conversion(
		isSameValue ? selectedRange.max + ONE_ITEM : selectedRange.max,
		country
	)

	const rangeLimit: RangePickerValue = useMemo(
		() => ({
			...selectedLimitRange,
			max:
				selectedLimitRange.min === selectedLimitRange.max
					? selectedLimitRange.max + ONE_ITEM
					: selectedLimitRange.max,
		}),
		[selectedLimitRange]
	)

	const endPosition = useMemo(() => {
		if ([ONE_ITEM, ZERO_ITEMS].includes(range)) {
			return 1
		}
		return (
			(selectedRange.max - rangeLimit.min) / (rangeLimit.max - rangeLimit.min)
		)
	}, [range, selectedRange.max, rangeLimit.min, rangeLimit.max])

	const handleFromChange = useCallback(
		(v: number) => setSelectedRange({ ...selectedRange, min: v }),
		[selectedRange, setSelectedRange]
	)

	const handleToChange = useCallback(
		(v: number) => setSelectedRange({ ...selectedRange, max: v }),
		[selectedRange, setSelectedRange]
	)

	const endRange = isSameValue ? DEFAULT_LIMIT : rangeLimit
	const endValue = isSameValue ? ONE_ITEM : selectedRange.max

	useEffect(() => {
		if (range < ZERO_ITEMS) {
			setSelectedRange(rangeLimit)
		}
	}, [setSelectedRange, rangeLimit, range])

	return (
		<div
			className={styles.root}
			style={
				{
					'--relative-start-position':
						(selectedRange.min - rangeLimit.min) /
						(rangeLimit.max - rangeLimit.min),
					'--relative-end-position': endPosition,
				} as CSSProperties
			}
		>
			<div className={styles.labelContainer}>
				<div
					className={styles.label}
					data-testid={dataTestIds.plpFilterDesktopRangeLabel('min')}
				>
					{minPrice}
				</div>
				<div
					className={cx(styles.label, styles.toLabel)}
					data-testid={dataTestIds.plpFilterDesktopRangeLabel('max')}
				>
					{maxPrice}
				</div>
			</div>
			<div className={styles.sliderContainer}>
				<Range
					range={rangeLimit}
					maxLimit={selectedRange.max - range * SPACE_PERCENTAGE}
					value={selectedRange.min}
					onChange={handleFromChange}
					aria-label={t('accessibility.filters.minPrice')}
					aria-valuetext={minPrice}
				/>
				<Range
					className={styles.toSlider}
					range={endRange}
					minLimit={selectedRange.min + range * SPACE_PERCENTAGE}
					value={endValue}
					onChange={handleToChange}
					aria-label={t('accessibility.filters.maxPrice')}
					aria-valuetext={maxPrice}
				/>
			</div>
		</div>
	)
}
